import { ChevronRight } from "@mui/icons-material";
import cn from "classnames";
import Link from "next/link";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import NoSSR from "react-no-ssr";
import { match } from "ts-pattern";

import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { LoadingSkeleton } from "@moment/design-system/LoadingSkeleton";

import { useAuth } from "~/auth/useAuth";
import { Editor } from "~/components/canvas/editor/BigRichTextEditor";
import { LoginButton } from "~/components/topbar/LoginButton";
import { Profile } from "~/components/topbar/Profile";
import { usePageData } from "~/state";
import { useSelectAllCellsReady } from "~/state/machines/cells";
import { LinkButton } from "~/ui/Button";
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarMenu,
	SidebarProvider,
	SidebarTrigger,
} from "~/ui/Sidebar";
import { ThemeSwitcher } from "~/ui/ThemeSwitcher";

import { useEasel } from "../../easel";
import { useEaselCurrentPage } from "../../hooks/useEaselCurrentPage";
import { makePageTree } from "../../navigation-pane/makePageTree";
import { StaticDocumentHead } from "../StaticDocumentHead";
import { SidebarMenuItemTree } from "./SidebarMenuItemTree";
import { useTableOfContents } from "./useTableOfContents";

export type CanvasProps = {
	canvas: DocumentMetadata;
};

/**
 * This renders a Moment document in the style of a VitePress technical docs website.
 */
export const TemplateVitePressDocs: FC<CanvasProps> = ({ canvas }) => {
	// Hooks
	const { isAuthenticated } = useAuth();

	const allCellsReady = useSelectAllCellsReady();
	const [isFirstPageLoad, setIsFirstPageLoad] = useState(true);

	const { workspaceName, documentId } = useEasel();
	const activePage = useEaselCurrentPage();
	const pageData = usePageData(documentId, activePage?.id ?? "");

	const pageTree = useMemo(
		() => makePageTree((canvas?.pagesMetadata ?? []).filter((page) => !page.archived)),
		[canvas.pagesMetadata]
	);

	const showSkeleton = useMemo(() => {
		if (isFirstPageLoad && allCellsReady) {
			/* if we are still on first page load and all cells are initialized then turn off "firstPageLoad" and return false */
			setIsFirstPageLoad(false);
		} else if (isFirstPageLoad && !allCellsReady) {
			/* if we are on first page load and cells are still loading */
			return true;
		}

		return false;
	}, [allCellsReady, isFirstPageLoad]);

	// Refs
	const editorContainerRef = useRef<HTMLDivElement>(null);

	const { headings, visibleHeading } = useTableOfContents();

	return (
		<>
			{/* ------ Client Meta Tags ------ */}
			<StaticDocumentHead canvas={canvas} pageData={pageData} pageSlug={activePage?.slug} />

			{/* ------ TOP BAR ------ */}
			<div className="flex overflow-y-auto">
				<SidebarProvider className="h-fit">
					<div className="flex grow bg-secondary w-full max-w-[0%] md:max-w-[35%] lg:max-w-[30%]">
						<div className="ml-auto md:pl-4 sticky top-0 h-fit">
							<Sidebar
								className="sticky top-0 !border-r-0 pr-[2rem] pl-1"
								insetX={false}
							>
								<div className="flex items-center border-b h-16">
									{canvas.title ? (
										<div
											className="text-md font-semibold truncate"
											title={canvas.title}
										>
											{canvas.title}
										</div>
									) : (
										<div className="text-md font-semibold text-tertiary italic">
											Untitled doc
										</div>
									)}
								</div>

								<SidebarContent className="py-4 -mx-1 px-1">
									<SidebarGroup className="!px-0">
										<SidebarMenu>
											{/* eslint-disable-next-line react/jsx-max-depth */}
											<SidebarMenuItemTree
												workspaceName={workspaceName}
												documentId={documentId}
												activePage={activePage}
												pageTree={pageTree}
												publishSettings={canvas.publishSettings}
											/>
										</SidebarMenu>
									</SidebarGroup>
								</SidebarContent>
							</Sidebar>
						</div>
					</div>

					{/* --- DOCUMENT STATE --- */}
					<div
						className={cn(
							"grow",
							"bg-primary",
							"min-h-screen",
							"h-fit",
							"max-w-screen",
							"w-full",
							"sm:w-fit",
							// IMPORTANT: Sidebar should slide *behind* the main content.
							"z-surface"
						)}
					>
						{/* IMPORTANT: z-component-raised so that this appears over the datagrid. */}
						<div className="max-w-[64rem] sticky top-0 left-0 flex flex-col bg-primary z-component-raised border-b">
							<div className="flex items-center h-16 shrink-0">
								<SidebarTrigger className="ml-4 md:hidden" />
								<div className="ml-auto gap-2 flex items-center pr-4 xl:pr-0">
									{canvas.publishSettings?.topbarLinks
										?.filter((link) => link != null)
										.map(({ name, url }) => (
											<LinkButton
												key={`${name}-${url}`}
												preset="quaternary"
												href={url}
												target="_blank"
											>
												{name}
											</LinkButton>
										))}
									{canvas.publishSettings?.topbarCtaButton && (
										<LinkButton
											preset="primary"
											href={
												canvas.publishSettings?.topbarCtaButton.url ?? "#"
											}
											target="_blank"
											borderRadius="full"
											endIcon={<ChevronRight />}
										>
											{canvas.publishSettings.topbarCtaButton.name}
										</LinkButton>
									)}
									<NoSSR>{isAuthenticated ? <Profile /> : <LoginButton />}</NoSSR>
									<ThemeSwitcher />
								</div>
							</div>
						</div>

						<div className="flex max-w-[58rem] mx-4 md:ml-[2rem] lg:ml-[4rem] xl:ml-[6rem] mt-12">
							<main
								className={cn(
									"mr-4",
									"md:mr-[2rem]",
									"lg:mr-[4rem]",
									"shrink",
									"grow",
									"max-w-full",
									// IMPORTANT: Override default scroll margin in
									// _prose-mirror-overrides.css, or clicking the table of
									// contents will hide the heading behind the top bar.
									"[&_[data-text-cell]]:scroll-mt-24",
									headings.length === 0 ? "xl:max-w-[54rem]" : "xl:max-w-[42rem]"
								)}
							>
								<h1 className="mb-12 text-3xl">{activePage?.title}</h1>
								{match({
									showSkeleton,
								})
									.with({ showSkeleton: true }, () => (
										<LoadingSkeleton
											barCount={10}
											randomizeWidths
											showChildren={!showSkeleton}
											SkeletonWrapper={({
												children,
											}: FC["arguments"]["children"]) => (
												<div className="canvas-content-row space-y-4">
													{children}
												</div>
											)}
										/>
									))
									.with({ showSkeleton: false }, () => (
										<Editor
											ref={editorContainerRef}
											publishTemplate="tech-docs-vitepress"
										/>
									))
									.exhaustive()}
							</main>

							{headings.length === 0 ? null : (
								<div className="grow bg-primary hidden xl:block max-w-[12rem] min-w-[12rem]">
									<div className="ml-auto sticky top-28 text-sm flex flex-col max-w-[12rem] max-h-[calc(100vh-7rem)] overflow-y-auto pb-2">
										<>
											<div className="flex items-center gap-2 my-2 font-medium">
												<div>On this page</div>
											</div>
											<div className="relative h-full overflow-y-auto">
												{headings.map(
													([level, heading, headingId], index) => (
														<SidebarHeading
															key={`${heading}-${index}`}
															level={level}
															heading={heading}
															headingId={headingId}
															visibleHeading={visibleHeading}
														/>
													)
												)}
												<div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-bg-primary to-transparent pointer-events-none" />
											</div>
										</>
									</div>
								</div>
							)}
						</div>
					</div>
				</SidebarProvider>
			</div>
		</>
	);
};

const SidebarHeading = ({
	level,
	heading,
	headingId,
	visibleHeading,
}: {
	level: number;
	heading: string;
	headingId: string;
	visibleHeading: string | null;
}) => {
	const ref = useRef<HTMLDivElement>(null);
	const isVisible = useMemo(() => headingId === visibleHeading, [headingId, visibleHeading]);

	/*
	 * When the heading is visible, scroll it into view.
	 */
	useEffect(() => {
		if (isVisible) {
			ref.current?.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [headingId, isVisible]);

	return (
		<div ref={ref} className="border-l py-1">
			<div
				className={cn("-ml-px", "border-l-[3px]", {
					"font-semibold border-brand": headingId === visibleHeading,
					"border-transparent": headingId !== visibleHeading,
				})}
				style={{
					paddingLeft: `${level}em`,
				}}
			>
				<Link
					id={`#${headingId}-link`}
					href={`#${headingId}`}
					className={cn(
						"text-primary",
						"no-underline",
						"hover:underline",
						"active:underline",
						"active:ring-0"
					)}
				>
					{heading}
				</Link>
			</div>
		</div>
	);
};
